<template>
  <div class="heatmap-container position-relative p-3 mt-3">
    <h3 class="heatmap-title">Heatmap Visualization</h3>
    <div class="slider-container">
          <input type="range" v-model="sliderValue" min="1" max="40" class="slider" />
          <div id="slider-value" class="slider-value">{{ sliderValue / 10.0 }} Stdv</div>
      </div> 
    <div class="heatmap-content">
      <div ref="vtkContainer" class="vtk-viewer mx-auto"></div> 
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import "@kitware/vtk.js/Rendering/Profiles/Geometry";
import vtkFullScreenRenderWindow from "@kitware/vtk.js/Rendering/Misc/FullScreenRenderWindow";
import vtkActor from "@kitware/vtk.js/Rendering/Core/Actor";
import vtkMapper from "@kitware/vtk.js/Rendering/Core/Mapper";
import vtkXMLPolyDataReader from "@kitware/vtk.js/IO/XML/XMLPolyDataReader";
import vtkLookupTable from "@kitware/vtk.js/Common/Core/LookupTable";
import vtkScalarBarActor from "@kitware/vtk.js/Rendering/Core/ScalarBarActor";

import html2canvas from "html2canvas";


export default {
  name: "HeatmapComponent",
  props: {
    polydataXML: {
      type: String,
      required: true,
    },
  },
  methods: {
    async captureHeatmapAsImage() {
      const vtkContainer = this.$refs.vtkContainer;
      if (vtkContainer) {
        const originalCanvas = await html2canvas(vtkContainer, { scale: 1 });
        const originalWidth = originalCanvas.width;
        const originalHeight = originalCanvas.height;
        const cropTop = 70; 
        const croppedHeight = originalHeight - cropTop;
        const croppedCanvas = document.createElement("canvas");
        croppedCanvas.width = originalWidth;
        croppedCanvas.height = croppedHeight;
        const ctx = croppedCanvas.getContext("2d");
        ctx.drawImage(
          originalCanvas,
          0,
          cropTop,
          originalWidth,
          croppedHeight,
          0,
          0,
          originalWidth,
          croppedHeight
        );

        return croppedCanvas.toDataURL("image/JPEG");
      }
      return null;
    },
  },
  setup(props) {
    const vtkContainer = ref(null);
    const context = ref(null);
    const sliderValue = ref(20); // Initial scale factor slider value

    const processPolydataXML = (xml) => {
      if (!xml) {
        console.warn("No polydata XML provided.");
        return;
      }

      try {
        // Clean XML data
        const cleanedXML = xml
          .replace(/^"/, "")
          .replace(/"$/, "")
          .replace(/\\n/g, " ")
          .replace(/\\/g, "");

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(cleanedXML, "application/xml");

        const parseError = xmlDoc.getElementsByTagName("parsererror");
        if (parseError.length) {
          console.error("Error parsing XML:", parseError[0].textContent);
          return;
        }

        const encoder = new TextEncoder();
        const xmlBuffer = encoder.encode(cleanedXML).buffer;

        // Parse polydata
        const polydataReader = vtkXMLPolyDataReader.newInstance();
        polydataReader.parseAsArrayBuffer(xmlBuffer);
        const polydataOutput = polydataReader.getOutputData(0);

        // Lookup table setup
        const lookupTable = vtkLookupTable.newInstance();
        lookupTable.setHueRange(0.66, 0); // From blue to red
        lookupTable.setValueRange(1, 1);
        lookupTable.setSaturationRange(1, 1);
        lookupTable.build();

        const polydataMapper = vtkMapper.newInstance();
        polydataMapper.setInputData(polydataOutput);
        polydataMapper.setLookupTable(lookupTable);
        polydataMapper.setColorByArrayName("Stdv"); // Set scalar array name
        polydataMapper.setScalarRange(-2.0, 2.0);

        // Handle scalar data for debugging
        const scalars = polydataOutput.getPointData().getScalars();
        const dataArray = scalars.getData();
        if (dataArray.some((value) => isNaN(value))) {
          console.error("Scalar data contains NaN values!");
        }

        // Actor setup
        const polydataActor = vtkActor.newInstance();
        polydataActor.setMapper(polydataMapper);

        // Scalar bar setup
        const scalarBar = vtkScalarBarActor.newInstance({
          boxSize: [80, 300], // Size of scalar bar
          axisTextStyle: { color: "black" },
          tickTextStyle: { color: "black" },
          drawNanAnnotation: false,
        });
        scalarBar.setScalarsToColors(lookupTable);
        scalarBar.setAxisLabel("Stdv");

        // VTK context setup
        const { renderer, renderWindow } = context.value;
        renderer.addActor(polydataActor);
        renderer.addActor(scalarBar);
        renderer.addActor(polydataActor);
        renderer.addActor(scalarBar);
        renderer.resetCamera();

        const camera = renderer.getActiveCamera();
        camera.zoom(0.9);
        renderer.resetCameraClippingRange();
        renderWindow.render();

        // Watch sliderValue for scale updates
        watch(sliderValue, (newValue) => {
          const scaleFactor = parseFloat(newValue);
          const newMax = scaleFactor / 10.0;
          const newMin = -newMax;
          polydataMapper.setScalarRange(newMin, newMax);
          renderWindow.render();
        });
      } catch (error) {
        console.error("Error processing polydata XML:", error);
      }
    };

    onMounted(() => {
      console.log("Initializing VTK render window...");

      const fullScreenRenderer = vtkFullScreenRenderWindow.newInstance({
        rootContainer: vtkContainer.value,
        containerStyle: { position: "relative" },
      });

      const renderer = fullScreenRenderer.getRenderer();
      const renderWindow = fullScreenRenderer.getRenderWindow();
      renderer.setBackground(1, 1, 1);

      context.value = { fullScreenRenderer, renderWindow, renderer };

      if (props.polydataXML) {
        processPolydataXML(props.polydataXML);
      }
    });

    onBeforeUnmount(() => {
    if (context.value) {
      // Safely delete the render window
      const fullScreenRenderWindow = context.value.fullScreenRenderWindow;
      if (fullScreenRenderWindow) {
        try {
          fullScreenRenderWindow.delete();
        } catch (error) {
          console.warn("Error during VTK cleanup:", error);
        }
      }
      // Reset context value to prevent lingering references
      context.value = null;
    }
  });

    return {
      vtkContainer,
      sliderValue,
    };
  },
};
</script>

<style scoped>
.vtk-container {
  width: 100%;
  height: 500px;
}

label {
  display: block;
  margin-top: 1em;
}

input[type="range"] {
  width: 100%;
}
</style>

<style scoped>
.heatmap-container {
  background-color: #f7f7f7c5;
  padding: 20px;
  border-radius: 8px;
  margin-left: 40px;
  margin-right: 40px;
}

.heatmap-title {
  text-align: left;
  margin-bottom: 10px;
  font-size: 1.2em;
  color: #333333;
  font-weight: bold;
}

.vtk-viewer {
  width: 50%;
  height: fit-content;
  padding: 1rem;
  margin: 0 auto;
}
.heatmap-content {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
}
.slider-container {
  margin: auto;
  width: 50%;
  display: flex;
  align-items: center;
}

.slider {
  margin-right: 10px;
}

.slider-value {
  font-size: 14px;
}
</style>
